import React, { createContext, useCallback, useContext, useState } from 'react';
import type { TrackedContract } from './ContractTrackingContext';
import type { ContractRef } from '../types/contractAnalytics';
import ContractSummary from '../components/analytics/ContractSummary';

interface ContractSummaryContextValue {
  openSummary: (
    contract: ContractRef,
    opts?: { simpleLayout?: boolean; tracked?: TrackedContract },
  ) => void;
  closeSummary: () => void;
}

const ContractSummaryContext = createContext<ContractSummaryContextValue | null>(null);

export function ContractSummaryProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [contract, setContract] = useState<ContractRef | null>(null);
  const [tracked, setTracked] = useState<TrackedContract | null>(null);
  const [simpleLayout, setSimpleLayout] = useState(false);

  const openSummary = useCallback(
    (c: ContractRef, opts?: { simpleLayout?: boolean; tracked?: TrackedContract }) => {
      setContract(c);
      setTracked(opts?.tracked ?? null);
      setSimpleLayout(Boolean(opts?.simpleLayout));
      setOpen(true);
    },
    [],
  );

  const closeSummary = useCallback(() => {
    setOpen(false);
    setContract(null);
    setTracked(null);
  }, []);

  return (
    <ContractSummaryContext.Provider value={{ openSummary, closeSummary }}>
      {children}
      {open && contract ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10050,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            justifyContent: simpleLayout ? 'center' : 'flex-end',
            alignItems: simpleLayout ? 'flex-end' : 'stretch',
          }}
          onClick={closeSummary}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: simpleLayout ? '100%' : 'min(520px, 96vw)',
              maxWidth: simpleLayout ? 480 : undefined,
              maxHeight: simpleLayout ? '88vh' : '100%',
              overflow: 'auto',
              background: 'var(--base, #0a0a0f)',
              borderLeft: simpleLayout ? undefined : '1px solid var(--hair, #333)',
              borderRadius: simpleLayout ? '16px 16px 0 0' : 0,
              boxShadow: '-8px 0 32px rgba(0,0,0,0.4)',
            }}
          >
            <ContractSummary
              contract={contract}
              tracked={tracked}
              simpleLayout={simpleLayout}
              embedded={false}
              onClose={closeSummary}
            />
          </div>
        </div>
      ) : null}
    </ContractSummaryContext.Provider>
  );
}

export function useContractSummary(): ContractSummaryContextValue {
  const ctx = useContext(ContractSummaryContext);
  if (!ctx) {
    return { openSummary: () => {}, closeSummary: () => {} };
  }
  return ctx;
}

/** Row → ContractRef helper for flow/scanner tables */
export function rowToContractRef(row: Record<string, unknown>): ContractRef | null {
  const symbol = String(row.Underlying || row.ticker || row.Ticker || row.symbol || '')
    .replace(/^\$/, '')
    .trim()
    .toUpperCase();
  const expiry = String(row['Expiry Date'] || row.expiry || '').slice(0, 10);
  const strike = parseFloat(String(row.Strike ?? row.strike ?? '').replace(/,/g, ''));
  const otRaw = String(row['Option Type'] || row.option_type || 'C').toUpperCase();
  const optionType: 'C' | 'P' = otRaw.startsWith('P') ? 'P' : 'C';
  if (!symbol || !expiry || !Number.isFinite(strike)) return null;
  return {
    symbol,
    strike,
    expiry,
    optionType,
    id: `${symbol}|${strike}|${expiry}|${optionType}`,
  };
}
